import { useState } from "react";
import { Loader2 } from "lucide-react";
import { KnowledgeNoteInputSchema } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/gb/field";
import { useCreateBrainNote } from "./brain-api";

/**
 * Add a hand-written note to the site's Brain.
 *
 * Notes are the only entries a person creates directly — everything else is
 * derived from a document, a decision or the journal. The server forces
 * `sourceType: "upload"` regardless of what is sent, which is also what makes
 * them the only entries deletable from this screen.
 */
export function AddNoteDialog({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [errors, setErrors] = useState<Partial<Record<"title" | "body", string>>>({});
  const create = useCreateBrainNote(projectId);

  const submit = async (): Promise<void> => {
    const parsed = KnowledgeNoteInputSchema.safeParse({
      title: title.trim(),
      body: body.trim(),
    });
    if (!parsed.success) {
      const next: Partial<Record<"title" | "body", string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "title" || key === "body") next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    await create.mutateAsync(parsed.data);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a note</DialogTitle>
          <DialogDescription>
            Something the site should remember that isn’t in a document — a gate code, a
            contact, a decision made on the phone.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-[18px]">
            <Field label="Title" error={errors.title}>
              {(props) => (
                <Input
                  {...props}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="North gate access"
                />
              )}
            </Field>
            <Field label="Note" error={errors.body}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={6}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Keypad is 4417 until handover. Facilities will reissue after."
                />
              )}
            </Field>
            {create.error && (
              <p role="alert" className="text-[0.85rem] font-semibold text-danger">
                {create.error.message}
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            className="hover:bg-brand-deep"
            disabled={create.isPending}
            onClick={() => void submit()}
          >
            {create.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
